import React, { useState, useEffect } from "react";
import AddSubModal from "./AddSubModal";
import {
  recruitJobsApis,
  rolesSettingsApis,
  departmentHRApi,
  employeeHRApis,
  BusinessSettingsApi,
} from "../../../Data/axios";
import {
  Button,
  TextField,
  InputAdornment,
  IconButton,
  Modal,
  Box,
  Typography,
  Slide,
  Grid,
  Checkbox,
  FormControlLabel,
  Chip,
  Autocomplete,
  Card,
  CardContent,
  MenuItem,
} from "@mui/material";
import {
  HelpOutline,
  FormatListBulleted,
  FormatListNumbered,
  FormatBold,
  FormatItalic,
  FormatUnderlined,
  StrikethroughS,
  InsertLink,
  Image,
  Code,
  FormatQuote,
  FormatClear,
} from "@mui/icons-material";

export default function AddJobModal({
  open,
  handleClose,
  handleSave,
  tableApis,
}) {
  const [noEndDate, setNoEndDate] = useState(false);
  const [salaryDisplayType, setSalaryDisplayType] = useState("range");

  // Add form state for all fields
  const [formData, setFormData] = useState({
    jobTitle: "",
    jobCategoryId: "",
    jobSubCategoryId: "",
    departmentId: "",
    skillIds: [],
    locationIds: [],
    interviewRoundIds: [],
    startDate: "",
    endDate: "",
    totalOpenings: "",
    status: "Open",
    recruiterId: "",
    jobTypeId: "",
    workExperience: "",
    currency: "USD ($)",
    minAmount: "",
    maxAmount: "",
    amount: "",
    isRemoteJob: 0,
    discloseSalary: 0,
    jobDescription: "",
    requiredPhoto: 0,
    requiredResume: 0,
    requiredDateOfBirth: 0,
    requiredGender: 0,
    requiredCurrentCTC: 0,
    requiredExpectedCTC: 0,
  });

  // States for API data
  const [jobTitles, setJobTitles] = useState([]);
  const [jobCategories, setJobCategories] = useState([]);
  const [jobSubCategories, setJobSubCategories] = useState([]);
  const [skills, setSkills] = useState([]);
  const [interviewRounds, setInterviewRounds] = useState([]);
  const [jobTypes, setJobTypes] = useState([]);
  const [recruiters, setRecruiters] = useState([]);
  const [departments, setDepartments] = useState([]);
  const [workExp, setWorkExp] = useState([]);
  const [locations, setLocations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [fieldErrors, setFieldErrors] = useState({}); // Add this new state

  const salaryDisplayOptions = [
    { value: "range", label: "Range" },
    { value: "starting", label: "Starting Salary" },
    { value: "maximum", label: "Maximum Salary" },
    { value: "exact", label: "Exact Salary" },
  ];

  // Fetch data on component mount
  useEffect(() => {
    if (open) {
      const fetchAllData = async () => {
        setLoading(true);

        try {
          // Fetch all data in parallel
          const [
            jobTitlesResponse,
            jobCategoriesResponse,
            jobSubCategoriesResponse,
            skillsResponse,
            interviewRoundsResponse,
            jobTypesResponse,
            rolesResponse,
            departmentsResponse,
            hrResponse,
            workExpResponse,
            locationsResponse,
          ] = await Promise.all([
            tableApis.getAllJobTitle(),
            tableApis.getAllJobCategories(),
            tableApis.getAllJobSubCategories(),
            tableApis.getSkills(),
            tableApis.getInterviewRounds(),
            tableApis.getJobTypes(),
            rolesSettingsApis.getAll(),
            departmentHRApi.getAll(),
            employeeHRApis.getAll(),
            tableApis.getWorkExperience(),
            BusinessSettingsApi.getAll(),
          ]);

          // Set state with the exact same data objects from the response
          setJobTitles(jobTitlesResponse.data.rjobs.data);
          setJobCategories(jobCategoriesResponse.data.categories.data);
          setJobSubCategories(jobSubCategoriesResponse.data.subcategories.data);
          setSkills(skillsResponse.data.skills.data);
          setInterviewRounds(interviewRoundsResponse.data.rounds.data);
          setJobTypes(jobTypesResponse.data.job_types.data);
          setDepartments(departmentsResponse.data);
          setRecruiters(hrResponse.data);
          setWorkExp(workExpResponse.data.work_experiences.data);
          setLocations(locationsResponse.data);
        } catch (err) {
          console.error("Error fetching data:", err);
          setError("Failed to fetch data. Please try again later.");
        } finally {
          setLoading(false);
        }
      };

      fetchAllData();
    }
  }, [open, tableApis]);

  // Update the validateForm function
  const validateForm = () => {
    const requiredFields = {
      jobTitle: "Job Title",
      jobCategoryId: "Job Category",
      jobSubCategoryId: "Job Sub Category",
      departmentId: "Department",
      skillIds: "Skills",
      locationIds: "Location",
      interviewRoundIds: "Interview Rounds",
      startDate: "Start Date",
      endDate: "End Date",
      totalOpenings: "Total Openings",
      recruiterId: "Recruiter",
      jobTypeId: "Job Type",
      workExperience: "Work Experience",
    };

    const errors = {};

    Object.entries(requiredFields).forEach(([field, label]) => {
      if (field === "endDate" && noEndDate) {
        return; // Skip end date validation if no end date is checked
      }

      if (Array.isArray(formData[field])) {
        if (formData[field].length === 0) {
          errors[field] = `${label} is required`;
        }
      } else if (!formData[field] || formData[field] === "--") {
        errors[field] = `${label} is required`;
      }
    });

    setFieldErrors(errors);
    return Object.keys(errors).length === 0;
  };

  // Handle form field changes
  const handleFormChange = (field, value) => {
    setFormData((prev) => ({
      ...prev,
      [field]: value,
    }));

    // Clear the error for this field when user starts filling it
    if (fieldErrors[field]) {
      setFieldErrors((prev) => ({
        ...prev,
        [field]: null,
      }));
    }
  };

  // Handle checkbox changes
  const handleCheckboxChange = (field, checked) => {
    setFormData((prev) => ({
      ...prev,
      [field]: checked ? 1 : 0,
    }));
  };

  // Handle form submission
  const handleSaveJob = async () => {
    if (!validateForm()) {
      return;
    }

    try {
      // Helper function to convert requirements checkboxes to binary string
      const getRequirementsString = () => {
        const requirements = [
          formData.requiredPhoto,
          formData.requiredResume,
          formData.requiredDateOfBirth,
          formData.requiredGender,
          formData.requiredCurrentCTC,
          formData.requiredExpectedCTC,
          0, // placeholder for additional requirement
          0, // placeholder for additional requirement
          0, // placeholder for additional requirement
        ];
        return requirements.join("");
      };

      // Prepare POST data in the new format
      const postData = {
        title: formData.jobTitle,
        salary: formData.discloseSalary,
        experience: formData.workExperience,
        job_category: parseInt(formData.jobCategoryId),
        job_subcategory: parseInt(formData.jobSubCategoryId),
        department: parseInt(formData.departmentId),
        skills: formData.skillIds.map((skill) => skill.id.toString()),
        location_data: formData.locationIds.map((location) =>
          location.id.toString()
        ),
        interview_rounds: formData.interviewRoundIds.map((round) =>
          round.id.toString()
        ),
        start_date: formData.startDate,
        end_date: noEndDate ? null : formData.endDate,
        vacancy: parseInt(formData.totalOpenings),
        status: formData.status === "Open" ? 1 : 0,
        job_type: parseInt(formData.jobTypeId),
        currency:
          formData.currency === "USD ($)"
            ? 1
            : formData.currency === "EUR (€)"
            ? 2
            : 3,
        pay_by:
          salaryDisplayType === "range"
            ? 1
            : salaryDisplayType === "starting"
            ? 2
            : salaryDisplayType === "maximum"
            ? 3
            : 4,
        min_salary:
          salaryDisplayType === "range"
            ? parseFloat(formData.minAmount) || 0
            : null,
        max_salary:
          salaryDisplayType === "range"
            ? parseFloat(formData.maxAmount) || 0
            : null,
        rate:
          salaryDisplayType !== "range"
            ? parseFloat(formData.amount) || 0
            : null,
        remote: formData.isRemoteJob,
        recruiter: formData.recruiterId,
        description: formData.jobDescription,
        requirements: getRequirementsString(),
      };

      const response = await tableApis.addJob(postData);
      console.log("API Response:", response);

      // Call the parent's handleSave function to refresh data
      if (handleSave) {
        await handleSave();
      }

      // Close modal after successful save
      handleCloseModal();
    } catch (error) {
      console.error("Error saving job:", error);
      alert("Error saving job. Please try again.");
    }
  };

  // Reset form when modal closes
  const handleCloseModal = () => {
    handleClose();
    setFormData({
      jobTitle: "",
      jobCategoryId: "",
      jobSubCategoryId: "",
      departmentId: "",
      skillIds: [],
      locationIds: [],
      interviewRoundIds: [],
      startDate: "",
      endDate: "",
      totalOpenings: "",
      status: "Open",
      recruiterId: "",
      jobTypeId: "",
      workExperience: "",
      currency: "USD ($)",
      minAmount: "",
      maxAmount: "",
      amount: "",
      isRemoteJob: 0,
      discloseSalary: 0,
      jobDescription: "",
      requiredPhoto: 0,
      requiredResume: 0,
      requiredDateOfBirth: 0,
      requiredGender: 0,
      requiredCurrentCTC: 0,
      requiredExpectedCTC: 0,
    });
    setNoEndDate(false);
    setSalaryDisplayType("range");
    setFieldErrors({}); // Clear all field errors
  };

  // Define reusable input styling
  const inputProps = {
    sx: {
      bgcolor: "#f9f9f9",
      "& .MuiOutlinedInput-root": {
        "& fieldset": {
          borderColor: "#e0e0e0",
        },
        "&:hover fieldset": {
          borderColor: "#bdbdbd",
        },
      },
      "& .MuiSelect-select": {
        color: "#424242", // For dropdown selects
      },
      "& .MuiInputBase-input": {
        color: "#424242", // For text and date inputs
      },
    },
  };

  const checkboxStyle = {
    color: "#9e9e9e",
    "&.Mui-checked": {
      color: "#4285F4",
    },
  };

  // Add a handler for salary display type change
  const handleSalaryDisplayChange = (event) => {
    setSalaryDisplayType(event.target.value);
  };

  // Add handlers for multi-select changes with error clearing
  const handleSkillsChange = (event, newValue) => {
    handleFormChange("skillIds", newValue);
  };

  const handleLocationsChange = (event, newValue) => {
    handleFormChange("locationIds", newValue);
  };

  const handleInterviewRoundsChange = (event, newValue) => {
    handleFormChange("interviewRoundIds", newValue);
  };

  // Add state for sub-modals
  const [subModalOpen, setSubModalOpen] = useState(false);
  const [subModalType, setSubModalType] = useState("");
  const [subModalTitle, setSubModalTitle] = useState("");

  // Add handlers for sub-modals
  const handleSubModalOpen = (type, title) => {
    setSubModalType(type);
    setSubModalTitle(title);
    setSubModalOpen(true);
  };

  const handleSubModalClose = () => {
    setSubModalOpen(false);
    setSubModalType("");
    setSubModalTitle("");
  };

  // Add API handlers for adding/deleting items
  const handleAddItem = async (itemName) => {
    try {
      let response;
      switch (subModalType) {
        case "jobCategory":
          response = await tableApis.addJobCategory({ name: itemName });
          // Refresh job categories
          const categoriesResponse = await tableApis.getAllJobCategories();
          setJobCategories(categoriesResponse.data.categories.data);
          break;
        case "jobSubCategory":
          if (!formData.jobCategoryId) {
            alert("Please select a job category first.");
            return;
          }
          response = await tableApis.addJobSubCategory({
            name: itemName,
            category_id: formData.jobCategoryId,
          });
          // Refresh job sub-categories
          const subCategoriesResponse =
            await tableApis.getAllJobSubCategories();
          setJobSubCategories(subCategoriesResponse.data.subcategories.data);
          break;
        case "jobType":
          response = await tableApis.addJobType({ name: itemName });
          // Refresh job types
          const jobTypesResponse = await tableApis.getJobTypes();
          setJobTypes(jobTypesResponse.data.job_types.data);
          break;
        case "workExperience":
          response = await tableApis.addWorkExperience({ name: itemName });
          const workExpResponse = await tableApis.getWorkExperience();
          setWorkExp(workExpResponse.data.work_experiences.data);
          break;
        case "interviewRound":
          response = await tableApis.addInterviewRound({
            name: itemName,
          });
          // Refresh interview rounds
          const roundsResponse = await tableApis.getInterviewRounds();
          setInterviewRounds(roundsResponse.data.rounds.data);
          break;
        default:
          console.warn("Unknown modal type:", subModalType);
      }
      console.log("Item added successfully:", response);
    } catch (error) {
      console.error("Error adding item:", error);
      throw error;
    }
  };

  const handleDeleteItem = async (item) => {
    try {
      let response;
      switch (subModalType) {
        case "jobCategory":
          response = await tableApis.deleteJobCategory(item.id);
          // Refresh job categories
          const categoriesResponse = await tableApis.getAllJobCategories();
          setJobCategories(categoriesResponse.data.categories.data);
          break;
        case "jobSubCategory":
          response = await tableApis.deleteJobSubCategory(item.id);
          // Refresh job sub-categories
          const subCategoriesResponse =
            await tableApis.getAllJobSubCategories();
          setJobSubCategories(subCategoriesResponse.data.subcategories.data);
          break;
        case "jobType":
          response = await tableApis.deleteJobType(item.id);
          // Refresh job types
          const jobTypesResponse = await tableApis.getJobTypes();
          setJobTypes(jobTypesResponse.data.job_types.data);
          break;
        case "interviewRound":
          response = await tableApis.deleteInterviewRound(item.id);
          // Refresh interview rounds
          const roundsResponse = await tableApis.getInterviewRounds();
          setInterviewRounds(roundsResponse.data.rounds.data);
          break;
        case "workExperience":
          response = await tableApis.deleteWorkExperience(item.id);
          // Refresh interview rounds
          const workExpResponse = await tableApis.getWorkExperience();
          setJobTypes(workExpResponse.data.work_experiences.data);
          break;
        default:
          console.warn("Unknown modal type:", subModalType);
      }
      console.log("Item deleted successfully:", response);
    } catch (error) {
      console.error("Error deleting item:", error);
      throw error;
    }
  };

  // Get current items for the modal
  const getCurrentItems = () => {
    switch (subModalType) {
      case "jobCategory":
        return jobCategories || [];
      case "jobSubCategory":
        return jobSubCategories || [];
      case "jobType":
        return jobTypes || [];
      case "workExperience":
        return workExp || [];
      case "interviewRound":
        return interviewRounds || [];
      default:
        return [];
    }
  };

  return (
    <>
      <Modal open={open} onClose={handleCloseModal} closeAfterTransition>
        <Slide direction="left" in={open}>
          <Box
            sx={{
              position: "absolute",
              top: 0,
              right: 0,
              width: "85%",
              height: "100%",
              bgcolor: "#f5f5f5",
              boxShadow: 24,
              p: 4,
              overflow: "auto",
            }}
          >
            <Box
              display="flex"
              justifyContent="space-between"
              alignItems="center"
              mb={3}
            >
              <Typography variant="h3" component="h2" sx={{ color: "#212121" }}>
                Job Details
              </Typography>
              <Button
                onClick={handleCloseModal}
                variant="outlined"
                sx={{
                  borderColor: "#bdbdbd",
                  color: "#424242",
                  "&:hover": {
                    borderColor: "#9e9e9e",
                    bgcolor: "rgba(0, 0, 0, 0.04)",
                  },
                }}
              >
                Close
              </Button>
            </Box>
            <Card
              sx={{
                p: 2,
                bgcolor: "#ffffff",
                boxShadow: "0px 2px 10px rgba(0, 0, 0, 0.08)",
              }}
            >
              <CardContent>
                <Box sx={{ mb: 4 }}>
                  <Grid container spacing={3}>
                    {/* Job Title */}
                    <Grid item xs={12} md={3}>
                      <Typography
                        variant="body1"
                        mb={1}
                        sx={{ color: "#212121" }}
                      >
                        Job Title <span style={{ color: "red" }}>*</span>
                      </Typography>
                      <TextField
                        fullWidth
                        placeholder="Job Title"
                        size="small"
                        value={formData.jobTitle}
                        onChange={(e) =>
                          handleFormChange("jobTitle", e.target.value)
                        }
                        error={!!fieldErrors.jobTitle}
                        InputProps={inputProps}
                      />
                      {fieldErrors.jobTitle && (
                        <Typography
                          variant="caption"
                          color="error"
                          sx={{ mt: 0.5, display: "block" }}
                        >
                          {fieldErrors.jobTitle}
                        </Typography>
                      )}
                    </Grid>

                    {/* Job Category */}
                    <Grid item xs={12} md={3}>
                      <Typography
                        variant="body1"
                        mb={1}
                        sx={{ color: "#212121" }}
                      >
                        Job Category <span style={{ color: "red" }}>*</span>
                      </Typography>
                      <Box display="flex" alignItems="center">
                        <Box sx={{ flex: 1 }}>
                          <TextField
                            select
                            fullWidth
                            value={formData.jobCategoryId}
                            onChange={(e) =>
                              handleFormChange("jobCategoryId", e.target.value)
                            }
                            size="small"
                            error={!!fieldErrors.jobCategoryId}
                            InputProps={inputProps}
                          >
                            <MenuItem value="">--</MenuItem>
                            {jobCategories &&
                              jobCategories.map((category) => (
                                <MenuItem key={category.id} value={category.id}>
                                  {category.name}
                                </MenuItem>
                              ))}
                          </TextField>
                          {fieldErrors.jobCategoryId && (
                            <Typography
                              variant="caption"
                              color="error"
                              sx={{ mt: 0.5, display: "block" }}
                            >
                              {fieldErrors.jobCategoryId}
                            </Typography>
                          )}
                        </Box>
                        <Button
                          onClick={() =>
                            handleSubModalOpen(
                              "jobCategory",
                              "Add Job Category"
                            )
                          }
                          sx={{
                            ml: 1,
                            border: "1px solid #e0e0e0",
                            color: "#424242",
                            "&:hover": {
                              bgcolor: "rgba(0, 0, 0, 0.04)",
                            },
                          }}
                        >
                          Add
                        </Button>
                      </Box>
                    </Grid>

                    {/* Job Sub Category */}
                    <Grid item xs={12} md={3}>
                      <Typography
                        variant="body1"
                        mb={1}
                        sx={{ color: "#212121" }}
                      >
                        Job Sub Category <span style={{ color: "red" }}>*</span>
                      </Typography>
                      <Box display="flex" alignItems="center">
                        <Box sx={{ flex: 1 }}>
                          <TextField
                            select
                            fullWidth
                            value={formData.jobSubCategoryId}
                            onChange={(e) =>
                              handleFormChange(
                                "jobSubCategoryId",
                                e.target.value
                              )
                            }
                            size="small"
                            error={!!fieldErrors.jobSubCategoryId}
                            InputProps={inputProps}
                          >
                            <MenuItem value="">--</MenuItem>
                            {jobSubCategories &&
                              jobSubCategories.map((category) => (
                                <MenuItem key={category.id} value={category.id}>
                                  {category.name}
                                </MenuItem>
                              ))}
                          </TextField>
                          {fieldErrors.jobSubCategoryId && (
                            <Typography
                              variant="caption"
                              color="error"
                              sx={{ mt: 0.5, display: "block" }}
                            >
                              {fieldErrors.jobSubCategoryId}
                            </Typography>
                          )}
                        </Box>
                        <Button
                          onClick={() =>
                            handleSubModalOpen(
                              "jobSubCategory",
                              "Add Job Sub Category"
                            )
                          }
                          sx={{
                            ml: 1,
                            border: "1px solid #e0e0e0",
                            color: "#424242",
                            "&:hover": {
                              bgcolor: "rgba(0, 0, 0, 0.04)",
                            },
                          }}
                        >
                          Add
                        </Button>
                      </Box>
                    </Grid>

                    {/* Department */}
                    <Grid item xs={12} md={3}>
                      <Typography
                        variant="body1"
                        mb={1}
                        sx={{ color: "#212121" }}
                      >
                        Department <span style={{ color: "red" }}>*</span>
                      </Typography>
                      <TextField
                        select
                        fullWidth
                        value={formData.departmentId}
                        onChange={(e) =>
                          handleFormChange("departmentId", e.target.value)
                        }
                        size="small"
                        error={!!fieldErrors.departmentId}
                        InputProps={inputProps}
                      >
                        <MenuItem value="">--</MenuItem>
                        {departments &&
                          departments.map((department) => (
                            <MenuItem key={department.id} value={department.id}>
                              {department.department}
                            </MenuItem>
                          ))}
                      </TextField>
                      {fieldErrors.departmentId && (
                        <Typography
                          variant="caption"
                          color="error"
                          sx={{ mt: 0.5, display: "block" }}
                        >
                          {fieldErrors.departmentId}
                        </Typography>
                      )}
                    </Grid>

                    {/* Skills */}
                    <Grid item xs={12} md={3}>
                      <Typography
                        variant="body1"
                        mb={1}
                        sx={{ color: "#212121" }}
                      >
                        Skills <span style={{ color: "red" }}>*</span>
                      </Typography>
                      <Autocomplete
                        multiple
                        options={skills || []}
                        getOptionLabel={(option) => option.name}
                        value={formData.skillIds}
                        onChange={handleSkillsChange}
                        renderTags={(value, getTagProps) =>
                          value.map((option, index) => (
                            <Chip
                              variant="outlined"
                              label={option.name}
                              size="small"
                              {...getTagProps({ index })}
                              sx={{
                                bgcolor: "#e3f2fd",
                                borderColor: "#90caf9",
                                mr: 0.5,
                                color: "#000000",
                                "& .MuiChip-label": {
                                  color: "#000000",
                                },
                                "& .MuiChip-deleteIcon": {
                                  color: "#1976d2",
                                  "&:hover": { color: "#1565c0" },
                                },
                              }}
                            />
                          ))
                        }
                        renderInput={(params) => (
                          <TextField
                            {...params}
                            placeholder="Select skills"
                            size="small"
                            error={!!fieldErrors.skillIds}
                            InputProps={{
                              ...params.InputProps,
                              ...inputProps,
                            }}
                          />
                        )}
                      />
                      {fieldErrors.skillIds && (
                        <Typography
                          variant="caption"
                          color="error"
                          sx={{ mt: 0.5, display: "block" }}
                        >
                          {fieldErrors.skillIds}
                        </Typography>
                      )}
                    </Grid>

                    {/* Location */}
                    <Grid item xs={12} md={3}>
                      <Typography
                        variant="body1"
                        mb={1}
                        sx={{ color: "#212121" }}
                      >
                        Location <span style={{ color: "red" }}>*</span>
                      </Typography>
                      <Autocomplete
                        multiple
                        options={locations || []}
                        getOptionLabel={(option) => option.location}
                        value={formData.locationIds}
                        onChange={handleLocationsChange}
                        renderTags={(value, getTagProps) =>
                          value.map((option, index) => (
                            <Chip
                              variant="outlined"
                              label={option.location}
                              size="small"
                              {...getTagProps({ index })}
                              sx={{
                                bgcolor: "#e8f5e9",
                                borderColor: "#a5d6a7",
                                mr: 0.5,
                                color: "#000000",
                                "& .MuiChip-label": {
                                  color: "#000000",
                                },
                                "& .MuiChip-deleteIcon": {
                                  color: "#388e3c",
                                  "&:hover": { color: "#2e7d32" },
                                },
                              }}
                            />
                          ))
                        }
                        renderInput={(params) => (
                          <TextField
                            {...params}
                            placeholder="Select locations"
                            size="small"
                            error={!!fieldErrors.locationIds}
                            InputProps={{
                              ...params.InputProps,
                              ...inputProps,
                            }}
                          />
                        )}
                      />
                      {fieldErrors.locationIds && (
                        <Typography
                          variant="caption"
                          color="error"
                          sx={{ mt: 0.5, display: "block" }}
                        >
                          {fieldErrors.locationIds}
                        </Typography>
                      )}
                    </Grid>

                    {/* Interview Rounds */}
                    <Grid item xs={12} md={3}>
                      <Typography
                        variant="body1"
                        mb={1}
                        sx={{ color: "#212121" }}
                      >
                        Interview Rounds <span style={{ color: "red" }}>*</span>
                      </Typography>
                      <Autocomplete
                        multiple
                        options={interviewRounds || []}
                        getOptionLabel={(option) => option.name}
                        value={formData.interviewRoundIds}
                        onChange={handleInterviewRoundsChange}
                        renderTags={(value, getTagProps) =>
                          value.map((option, index) => (
                            <Chip
                              variant="outlined"
                              label={option.name}
                              size="small"
                              {...getTagProps({ index })}
                              sx={{
                                bgcolor: "#fff8e1",
                                borderColor: "#ffe082",
                                mr: 0.5,
                                color: "#000000",
                                "& .MuiChip-label": {
                                  color: "#000000",
                                },
                                "& .MuiChip-deleteIcon": {
                                  color: "#ff8f00",
                                  "&:hover": { color: "#ff6f00" },
                                },
                              }}
                            />
                          ))
                        }
                        renderInput={(params) => (
                          <TextField
                            {...params}
                            placeholder="Select interview rounds"
                            size="small"
                            error={!!fieldErrors.interviewRoundIds}
                            InputProps={{
                              ...params.InputProps,
                              ...inputProps,
                            }}
                          />
                        )}
                      />
                      {fieldErrors.interviewRoundIds && (
                        <Typography
                          variant="caption"
                          color="error"
                          sx={{ mt: 0.5, display: "block" }}
                        >
                          {fieldErrors.interviewRoundIds}
                        </Typography>
                      )}
                    </Grid>

                    {/* Start Date */}
                    <Grid item xs={12} md={3}>
                      <Typography
                        variant="body1"
                        mb={1}
                        sx={{ color: "#424242" }}
                      >
                        Start Date <span style={{ color: "red" }}>*</span>
                      </Typography>
                      <TextField
                        type="date"
                        fullWidth
                        size="small"
                        value={formData.startDate}
                        onChange={(e) =>
                          handleFormChange("startDate", e.target.value)
                        }
                        error={!!fieldErrors.startDate}
                        InputProps={inputProps}
                      />
                      {fieldErrors.startDate && (
                        <Typography
                          variant="caption"
                          color="error"
                          sx={{ mt: 0.5, display: "block" }}
                        >
                          {fieldErrors.startDate}
                        </Typography>
                      )}
                    </Grid>

                    {/* End Date */}
                    <Grid item xs={12} md={3}>
                      <Typography
                        variant="body1"
                        mb={1}
                        sx={{ color: "#212121" }}
                      >
                        End Date <span style={{ color: "red" }}>*</span>
                      </Typography>
                      <Box display="flex" alignItems="center">
                        {!noEndDate && (
                          <Box sx={{ flex: 1 }}>
                            <TextField
                              type="date"
                              fullWidth
                              size="small"
                              value={formData.endDate}
                              onChange={(e) =>
                                handleFormChange("endDate", e.target.value)
                              }
                              error={!!fieldErrors.endDate}
                              InputProps={inputProps}
                            />
                            {fieldErrors.endDate && (
                              <Typography
                                variant="caption"
                                color="error"
                                sx={{ mt: 0.5, display: "block" }}
                              >
                                {fieldErrors.endDate}
                              </Typography>
                            )}
                          </Box>
                        )}
                      </Box>
                      <FormControlLabel
                        control={
                          <Checkbox
                            sx={checkboxStyle}
                            checked={noEndDate}
                            onChange={(e) => setNoEndDate(e.target.checked)}
                          />
                        }
                        label="No End Date"
                        sx={{ ml: 1, color: "#757575" }}
                      />
                    </Grid>

                    {/* Total Openings */}
                    <Grid item xs={12} md={3}>
                      <Typography
                        variant="body1"
                        mb={1}
                        sx={{ color: "#212121" }}
                      >
                        Total Openings <span style={{ color: "red" }}>*</span>
                      </Typography>
                      <TextField
                        fullWidth
                        size="small"
                        type="number"
                        value={formData.totalOpenings}
                        onChange={(e) =>
                          handleFormChange("totalOpenings", e.target.value)
                        }
                        error={!!fieldErrors.totalOpenings}
                        InputProps={inputProps}
                      />
                      {fieldErrors.totalOpenings && (
                        <Typography
                          variant="caption"
                          color="error"
                          sx={{ mt: 0.5, display: "block" }}
                        >
                          {fieldErrors.totalOpenings}
                        </Typography>
                      )}
                    </Grid>

                    {/* Status */}
                    <Grid item xs={12} md={3}>
                      <Typography
                        variant="body1"
                        mb={1}
                        sx={{ color: "#212121" }}
                      >
                        Status
                      </Typography>
                      <TextField
                        select
                        fullWidth
                        value={formData.status}
                        onChange={(e) =>
                          handleFormChange("status", e.target.value)
                        }
                        size="small"
                        InputProps={inputProps}
                      >
                        <MenuItem value="Open">
                          <Box display="flex" alignItems="center">
                            <Box
                              component="span"
                              sx={{
                                width: 10,
                                height: 10,
                                borderRadius: "50%",
                                bgcolor: "#4caf50",
                                display: "inline-block",
                                mr: 1,
                              }}
                            />
                            Open
                          </Box>
                        </MenuItem>
                      </TextField>
                    </Grid>

                    {/* Recruiter */}
                    <Grid item xs={12} md={3}>
                      <Typography
                        variant="body1"
                        mb={1}
                        sx={{ color: "#212121" }}
                      >
                        Recruiter <span style={{ color: "red" }}>*</span>
                      </Typography>
                      <Box display="flex" alignItems="center">
                        <Box sx={{ flex: 1 }}>
                          <TextField
                            select
                            fullWidth
                            value={formData.recruiterId}
                            onChange={(e) =>
                              handleFormChange("recruiterId", e.target.value)
                            }
                            size="small"
                            error={!!fieldErrors.recruiterId}
                            InputProps={inputProps}
                          >
                            <MenuItem value="">--</MenuItem>
                            {recruiters &&
                              recruiters.map((recruiter) => (
                                <MenuItem
                                  key={recruiter.empid}
                                  value={recruiter.empid}
                                >
                                  {recruiter.empid}
                                </MenuItem>
                              ))}
                          </TextField>
                          {fieldErrors.recruiterId && (
                            <Typography
                              variant="caption"
                              color="error"
                              sx={{ mt: 0.5, display: "block" }}
                            >
                              {fieldErrors.recruiterId}
                            </Typography>
                          )}
                        </Box>
                        <Button
                          sx={{
                            ml: 1,
                            border: "1px solid #e0e0e0",
                            color: "#424242",
                            "&:hover": {
                              bgcolor: "rgba(0, 0, 0, 0.04)",
                            },
                          }}
                        >
                          Add
                        </Button>
                      </Box>
                    </Grid>

                    {/* Job Type */}
                    <Grid item xs={12} md={3}>
                      <Typography
                        variant="body1"
                        mb={1}
                        sx={{ color: "#212121" }}
                      >
                        Job Type <span style={{ color: "red" }}>*</span>
                      </Typography>
                      <Box display="flex" alignItems="center">
                        <Box sx={{ flex: 1 }}>
                          <TextField
                            select
                            fullWidth
                            value={formData.jobTypeId}
                            onChange={(e) =>
                              handleFormChange("jobTypeId", e.target.value)
                            }
                            size="small"
                            error={!!fieldErrors.jobTypeId}
                            InputProps={inputProps}
                          >
                            <MenuItem value="">--</MenuItem>
                            {jobTypes &&
                              jobTypes.map((category) => (
                                <MenuItem key={category.id} value={category.id}>
                                  {category.name}
                                </MenuItem>
                              ))}
                          </TextField>
                          {fieldErrors.jobTypeId && (
                            <Typography
                              variant="caption"
                              color="error"
                              sx={{ mt: 0.5, display: "block" }}
                            >
                              {fieldErrors.jobTypeId}
                            </Typography>
                          )}
                        </Box>
                        <Button
                          onClick={() =>
                            handleSubModalOpen("jobType", "Add Job Type")
                          }
                          sx={{
                            ml: 1,
                            border: "1px solid #e0e0e0",
                            color: "#424242",
                            "&:hover": {
                              bgcolor: "rgba(0, 0, 0, 0.04)",
                            },
                          }}
                        >
                          Add
                        </Button>
                      </Box>
                    </Grid>

                    {/* Work Experience */}
                    <Grid item xs={12} md={3}>
                      <Typography
                        variant="body1"
                        mb={1}
                        sx={{ color: "#212121" }}
                      >
                        Work Experience <span style={{ color: "red" }}>*</span>
                      </Typography>
                      <Box display="flex" alignItems="center">
                        <Box sx={{ flex: 1 }}>
                          <TextField
                            select
                            fullWidth
                            value={formData.workExperience}
                            onChange={(e) =>
                              handleFormChange("workExperience", e.target.value)
                            }
                            size="small"
                            error={!!fieldErrors.workExperience}
                            InputProps={inputProps}
                          >
                            <MenuItem value="">--</MenuItem>
                            {workExp &&
                              workExp.map((experience) => (
                                <MenuItem
                                  key={experience.id}
                                  value={experience.id}
                                >
                                  {experience.name}
                                </MenuItem>
                              ))}
                          </TextField>
                          {fieldErrors.workExperience && (
                            <Typography
                              variant="caption"
                              color="error"
                              sx={{ mt: 0.5, display: "block" }}
                            >
                              {fieldErrors.workExperience}
                            </Typography>
                          )}
                        </Box>
                        <Button
                          onClick={() =>
                            handleSubModalOpen(
                              "workExperience",
                              "Add Work Experience"
                            )
                          }
                          sx={{
                            ml: 1,
                            border: "1px solid #e0e0e0",
                            color: "#424242",
                            "&:hover": {
                              bgcolor: "rgba(0, 0, 0, 0.04)",
                            },
                          }}
                        >
                          Add
                        </Button>
                      </Box>
                    </Grid>

                    {/* Currency */}
                    <Grid item xs={12} md={3}>
                      <Typography
                        variant="body1"
                        mb={1}
                        sx={{ color: "#212121" }}
                      >
                        Currency
                      </Typography>
                      <TextField
                        select
                        fullWidth
                        value={formData.currency}
                        onChange={(e) =>
                          handleFormChange("currency", e.target.value)
                        }
                        size="small"
                        InputProps={inputProps}
                      >
                        <MenuItem value="USD ($)">USD ($)</MenuItem>
                        <MenuItem value="EUR (€)">EUR (€)</MenuItem>
                        <MenuItem value="INR (₹)">INR (₹)</MenuItem>
                      </TextField>
                    </Grid>

                    {/* Show Pay By */}
                    <Grid item xs={12} md={3}>
                      <Typography
                        variant="body1"
                        mb={1}
                        sx={{ color: "#212121" }}
                      >
                        Show Pay By
                      </Typography>
                      <TextField
                        select
                        fullWidth
                        value={salaryDisplayType}
                        onChange={handleSalaryDisplayChange}
                        size="small"
                        InputProps={inputProps}
                      >
                        {salaryDisplayOptions.map((option) => (
                          <MenuItem key={option.value} value={option.value}>
                            {option.label}
                          </MenuItem>
                        ))}
                      </TextField>
                    </Grid>

                    {/* Conditional Salary Input Fields */}
                    {salaryDisplayType === "range" ? (
                      <>
                        <Grid item xs={12} md={3}>
                          <Typography
                            variant="body1"
                            mb={1}
                            sx={{ color: "#212121" }}
                          >
                            Min Amount
                          </Typography>
                          <TextField
                            fullWidth
                            size="small"
                            type="number"
                            placeholder="0"
                            value={formData.minAmount}
                            onChange={(e) =>
                              handleFormChange("minAmount", e.target.value)
                            }
                            InputProps={{
                              ...inputProps,
                              startAdornment: (
                                <InputAdornment position="start">
                                  $
                                </InputAdornment>
                              ),
                            }}
                          />
                        </Grid>
                        <Grid item xs={12} md={3}>
                          <Typography
                            variant="body1"
                            mb={1}
                            sx={{ color: "#212121" }}
                          >
                            Max Amount
                          </Typography>
                          <TextField
                            fullWidth
                            size="small"
                            type="number"
                            placeholder="0"
                            value={formData.maxAmount}
                            onChange={(e) =>
                              handleFormChange("maxAmount", e.target.value)
                            }
                            InputProps={{
                              ...inputProps,
                              startAdornment: (
                                <InputAdornment position="start">
                                  $
                                </InputAdornment>
                              ),
                            }}
                          />
                        </Grid>
                      </>
                    ) : (
                      <Grid item xs={12} md={3}>
                        <Typography
                          variant="body1"
                          mb={1}
                          sx={{ color: "#212121" }}
                        >
                          Amount
                        </Typography>
                        <TextField
                          fullWidth
                          size="small"
                          type="number"
                          placeholder="0"
                          value={formData.amount}
                          onChange={(e) =>
                            handleFormChange("amount", e.target.value)
                          }
                          InputProps={{
                            ...inputProps,
                            startAdornment: (
                              <InputAdornment position="start">
                                $
                              </InputAdornment>
                            ),
                          }}
                        />
                      </Grid>
                    )}

                    {/* Remote Job and Disclose Salary Checkboxes */}
                    <Grid
                      item
                      xs={12}
                      md={salaryDisplayType === "range" ? 3 : 6}
                    >
                      <Box sx={{ mt: 4 }}>
                        <FormControlLabel
                          control={
                            <Checkbox
                              sx={checkboxStyle}
                              checked={formData.isRemoteJob === 1}
                              onChange={(e) =>
                                handleCheckboxChange(
                                  "isRemoteJob",
                                  e.target.checked
                                )
                              }
                            />
                          }
                          label="Is this a remote Job?"
                          sx={{ color: "#757575" }}
                        />
                      </Box>
                      <Box>
                        <FormControlLabel
                          control={
                            <Checkbox
                              sx={checkboxStyle}
                              checked={formData.discloseSalary === 1}
                              onChange={(e) =>
                                handleCheckboxChange(
                                  "discloseSalary",
                                  e.target.checked
                                )
                              }
                            />
                          }
                          label="Disclose Salary on career"
                          sx={{ color: "#757575" }}
                        />
                      </Box>
                    </Grid>

                    {/* Job Description */}
                    <Grid item xs={12}>
                      <Typography
                        variant="body1"
                        mb={1}
                        sx={{ color: "#212121" }}
                      >
                        Job Description
                      </Typography>
                      <Card
                        variant="outlined"
                        sx={{
                          p: 1,
                          bgcolor: "#ffffff",
                          border: "1px solid #e0e0e0",
                          boxShadow: "inset 0px 0px 5px rgba(0, 0, 0, 0.05)",
                        }}
                      >
                        <Box sx={{ mb: 1, display: "flex", gap: 1 }}>
                          <TextField
                            select
                            size="small"
                            defaultValue="Normal"
                            sx={{
                              minWidth: 120,
                              "& .MuiInputBase-root": {
                                color: "#424242",
                                borderColor: "#bdbdbd",
                              },
                            }}
                          >
                            <MenuItem value="Normal">Normal</MenuItem>
                          </TextField>
                          <IconButton size="small" sx={{ color: "#616161" }}>
                            <FormatListBulleted />
                          </IconButton>
                          <IconButton size="small" sx={{ color: "#616161" }}>
                            <FormatListNumbered />
                          </IconButton>
                          <IconButton size="small" sx={{ color: "#616161" }}>
                            <FormatBold />
                          </IconButton>
                          <IconButton size="small" sx={{ color: "#616161" }}>
                            <FormatItalic />
                          </IconButton>
                          <IconButton size="small" sx={{ color: "#616161" }}>
                            <FormatUnderlined />
                          </IconButton>
                          <IconButton size="small" sx={{ color: "#616161" }}>
                            <StrikethroughS />
                          </IconButton>
                          <IconButton size="small" sx={{ color: "#616161" }}>
                            <InsertLink />
                          </IconButton>
                          <IconButton size="small" sx={{ color: "#616161" }}>
                            <Image />
                          </IconButton>
                          <IconButton size="small" sx={{ color: "#616161" }}>
                            <Code />
                          </IconButton>
                          <IconButton size="small" sx={{ color: "#616161" }}>
                            <FormatQuote />
                          </IconButton>
                          <IconButton size="small" sx={{ color: "#616161" }}>
                            <FormatClear />
                          </IconButton>
                        </Box>
                        <TextField
                          multiline
                          rows={6}
                          fullWidth
                          variant="outlined"
                          value={formData.jobDescription}
                          onChange={(e) =>
                            handleFormChange("jobDescription", e.target.value)
                          }
                          sx={{
                            "& fieldset": {
                              border: "1px solid #e0e0e0",
                              borderRadius: "4px",
                            },
                            "& .MuiInputBase-root": {
                              color: "#212121",
                            },
                            bgcolor: "#f9f9f9",
                          }}
                        />
                      </Card>
                    </Grid>

                    {/* Required Fields */}
                    <Grid item xs={12}>
                      <Box sx={{ mt: 2 }}>
                        <Typography
                          variant="body1"
                          mb={1}
                          display="flex"
                          alignItems="center"
                          sx={{ color: "#212121" }}
                        >
                          Required Fields{" "}
                          <HelpOutline
                            fontSize="small"
                            sx={{ ml: 1, color: "#757575" }}
                          />
                        </Typography>
                        <Box display="flex" flexWrap="wrap" gap={3}>
                          <FormControlLabel
                            control={
                              <Checkbox
                                sx={checkboxStyle}
                                checked={formData.requiredPhoto === 1}
                                onChange={(e) =>
                                  handleCheckboxChange(
                                    "requiredPhoto",
                                    e.target.checked
                                  )
                                }
                              />
                            }
                            label="Photo"
                            sx={{ color: "#757575" }}
                          />
                          <FormControlLabel
                            control={
                              <Checkbox
                                sx={checkboxStyle}
                                checked={formData.requiredResume === 1}
                                onChange={(e) =>
                                  handleCheckboxChange(
                                    "requiredResume",
                                    e.target.checked
                                  )
                                }
                              />
                            }
                            label="Resume"
                            sx={{ color: "#757575" }}
                          />
                          <FormControlLabel
                            control={
                              <Checkbox
                                sx={checkboxStyle}
                                checked={formData.requiredDateOfBirth === 1}
                                onChange={(e) =>
                                  handleCheckboxChange(
                                    "requiredDateOfBirth",
                                    e.target.checked
                                  )
                                }
                              />
                            }
                            label="Date Of Birth"
                            sx={{ color: "#757575" }}
                          />
                          <FormControlLabel
                            control={
                              <Checkbox
                                sx={checkboxStyle}
                                checked={formData.requiredGender === 1}
                                onChange={(e) =>
                                  handleCheckboxChange(
                                    "requiredGender",
                                    e.target.checked
                                  )
                                }
                              />
                            }
                            label="Gender"
                            sx={{ color: "#757575" }}
                          />
                          <FormControlLabel
                            control={
                              <Checkbox
                                sx={checkboxStyle}
                                checked={formData.requiredCurrentCTC === 1}
                                onChange={(e) =>
                                  handleCheckboxChange(
                                    "requiredCurrentCTC",
                                    e.target.checked
                                  )
                                }
                              />
                            }
                            label="Current CTC"
                            sx={{ color: "#757575" }}
                          />
                          <FormControlLabel
                            control={
                              <Checkbox
                                sx={checkboxStyle}
                                checked={formData.requiredExpectedCTC === 1}
                                onChange={(e) =>
                                  handleCheckboxChange(
                                    "requiredExpectedCTC",
                                    e.target.checked
                                  )
                                }
                              />
                            }
                            label="Expected CTC"
                            sx={{ color: "#757575" }}
                          />
                        </Box>
                      </Box>
                    </Grid>
                  </Grid>
                </Box>

                {/* Form buttons */}
                <Box display="flex" gap={2} mt={3}>
                  <Button
                    variant="contained"
                    onClick={handleSaveJob}
                    sx={{
                      bgcolor: "#4285F4",
                      color: "white",
                      "&:hover": { bgcolor: "#3367D6" },
                      fontWeight: 500,
                      textTransform: "none",
                      padding: "8px 16px",
                    }}
                  >
                    Save
                  </Button>
                  <Button
                    variant="outlined"
                    onClick={handleCloseModal}
                    sx={{
                      borderColor: "#bdbdbd",
                      color: "#424242",
                      "&:hover": {
                        borderColor: "#9e9e9e",
                        bgcolor: "rgba(0, 0, 0, 0.04)",
                      },
                      textTransform: "none",
                      padding: "8px 16px",
                    }}
                  >
                    Cancel
                  </Button>
                </Box>
              </CardContent>
            </Card>
          </Box>
        </Slide>
      </Modal>

      {/* Add the SubModal component */}
      <AddSubModal
        open={subModalOpen}
        onClose={handleSubModalClose}
        title={subModalTitle}
        items={getCurrentItems()}
        onAdd={handleAddItem}
        onDelete={handleDeleteItem}
      />
    </>
  );
}
